/**
 * SectorPreviewSwitcher — Admin/Suporte-only.
 *
 * v0.99.48 · Fev/2026 — refatoração visual premium:
 *   • Zero emoji / ícone de categoria (apenas um discreto chevron).
 *   • Paleta slate/dark (sem bege/amber).
 *   • Agrupamento por família de setor na ordem canônica do produto
 *     (automotivo → saúde → profissionais → alimentação → varejo →
 *     logística → serviços gerais → comunidade & fé → mandato &
 *     governo → gestão pública).
 *   • Mostra TODOS os 29 setores ativos vindos de `/api/sector/list`.
 *   • Setor que ainda não existir no backend é silenciosamente omitido
 *     (não quebra o layout).
 *
 * Visibilidade (inalterada):
 *   • user.role === "super_admin"      (master)
 *   • user.impersonated_by              (sessão impersonated)
 */
import { useEffect, useMemo, useState } from "react";
import { CaretDown, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSector, setSectorPreviewCode } from "@/lib/sectorContext";
import { useAuth } from "@/contexts/AuthContext";

// ── Taxonomia canônica (mesma do site). Cada família lista os
// códigos dos setores dela. Códigos que não existem no backend
// são silenciosamente ignorados.
const SECTOR_FAMILIES = [
  {
    id: "automotive",
    label: "Automotivo",
    hint: "Oficinas e concessionárias",
    codes: ["automotive", "automotive_dealer"],
  },
  {
    id: "health",
    label: "Saúde & Bem-estar",
    hint: "Clínicas, beleza, pets, academia, farmácia",
    codes: ["clinic", "salon", "petshop", "gym", "pharmacy"],
  },
  {
    id: "professional",
    label: "Serviços Profissionais",
    hint: "Contabilidade, advocacia, imóveis, seguros, educação",
    codes: ["accounting_firm", "law_firm", "real_estate",
             "insurance_broker", "school"],
  },
  {
    id: "food_hospitality",
    label: "Alimentação & Hospedagem",
    hint: "Restaurantes, hotéis, eventos, mercados",
    codes: ["restaurant", "hotel", "events", "supermarket"],
  },
  {
    id: "retail",
    label: "Varejo & Comércio",
    hint: "Construção, móveis, atacado, indústria",
    codes: ["construction_supply", "furniture_store",
             "distributor", "industry"],
  },
  {
    id: "logistics_repair",
    label: "Logística & Reparo",
    hint: "Transportadora, assistência técnica",
    codes: ["logistics", "tech_repair"],
  },
  {
    id: "general_services",
    label: "Serviços Gerais",
    hint: "Condomínio, administradoras, prestadoras",
    codes: ["condominium", "service_provider"],
  },
  {
    id: "faith",
    label: "Comunidade & Fé",
    hint: "Igrejas e templos religiosos",
    codes: ["church"],
  },
  {
    id: "mandate",
    label: "Mandato & Governo",
    hint: "Gabinetes, representantes, estrutura política",
    codes: ["mandate"],
  },
  {
    id: "public_management",
    label: "Gestão Pública",
    hint: "Prefeituras, órgãos públicos, secretarias",
    codes: ["gov_municipal", "gov_secretariat",
             "gov_chamber", "gov_state"],
  },
];


export default function SectorPreviewSwitcher() {
  const { user } = useAuth();
  const { previewCode, profile, isPreviewMode } = useSector();
  const [open, setOpen] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(false);

  const canUse =
    user?.role === "super_admin" || !!user?.impersonated_by;

  useEffect(() => {
    if (!canUse || sectors.length > 0) return;
    let cancelled = false;
    setLoading(true);
    api.get("/sector/list")
      .then(({ data }) => {
        if (!cancelled) {
          setSectors(Array.isArray(data?.sectors) ? data.sectors : []);
        }
      })
      .catch(() => { if (!cancelled) setSectors([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canUse, sectors.length]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest?.("[data-testid='sector-preview-switcher']")) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Index por código para lookup O(1) ao montar os grupos.
  const byCode = useMemo(() => {
    const m = {};
    for (const s of sectors) m[s.code] = s;
    return m;
  }, [sectors]);

  // Monta grupos preservando a ordem e filtrando códigos ausentes.
  const grouped = useMemo(() => {
    return SECTOR_FAMILIES
      .map((fam) => ({
        ...fam,
        items: fam.codes
          .map((c) => byCode[c])
          .filter(Boolean),
      }))
      .filter((fam) => fam.items.length > 0);
  }, [byCode]);

  // Soma de setores exibidos (útil pro tooltip e debug visual).
  const totalShown = useMemo(
    () => grouped.reduce((n, f) => n + f.items.length, 0),
    [grouped],
  );

  if (!canUse) return null;

  const currentCode = previewCode || profile?.code || "automotive";
  const currentLabel = profile?.display_name || "Automotivo";

  const pick = (code) => {
    setSectorPreviewCode(code === "__real__" ? null : code);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="sector-preview-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="sector-preview-switcher-btn"
        className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
          isPreviewMode
            ? "border-slate-900 bg-slate-900 text-white hover:bg-slate-800"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
        }`}
        title={
          isPreviewMode
            ? `Preview ativo · ${currentLabel}`
            : `Simular setor (admin · ${totalShown} categorias)`
        }
      >
        <span className="font-semibold tracking-wide text-[10px] uppercase">
          Setor
        </span>
        <span className="hidden sm:inline opacity-80">·</span>
        <span className="hidden sm:inline truncate max-w-[14ch]">
          {currentLabel}
        </span>
        <CaretDown size={9} weight="bold" className="opacity-70" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[42rem] max-w-[90vw] rounded-xl bg-white border border-slate-200 shadow-xl z-50 max-h-[82vh] overflow-y-auto"
          data-testid="sector-preview-switcher-menu"
        >
          <div className="px-4 py-3 border-b border-slate-100 flex items-start justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                Simular setor · Admin
              </div>
              <div className="text-[11.5px] text-slate-500 leading-snug mt-0.5">
                Troca a UI para o segmento escolhido. Nada é gravado no banco.
              </div>
            </div>
            <div className="text-[10px] text-slate-400 whitespace-nowrap pt-0.5">
              {totalShown} setores
            </div>
          </div>

          {isPreviewMode && (
            <button
              type="button"
              onClick={() => pick("__real__")}
              data-testid="sector-preview-exit"
              className="w-full flex items-center gap-2 px-4 py-2.5 text-[12.5px] text-slate-800 hover:bg-slate-50 border-b border-slate-100"
            >
              <X size={12} weight="bold" className="text-slate-500" />
              <span>Sair do preview — voltar ao setor real</span>
            </button>
          )}

          {loading ? (
            <div className="px-4 py-5 text-[11.5px] text-slate-500 italic">
              Carregando categorias…
            </div>
          ) : grouped.length === 0 ? (
            <div className="px-4 py-5 text-[11.5px] text-slate-500 italic">
              Nenhum setor cadastrado no backend.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 px-2 py-2">
              {grouped.map((fam) => (
                <div
                  key={fam.id}
                  className="rounded-lg border border-slate-100 bg-white hover:border-slate-200 transition-colors p-2"
                  data-testid={`sector-preview-group-${fam.id}`}
                >
                  <div className="px-1 pb-1">
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-900 font-semibold">
                      {fam.label}
                    </div>
                  </div>
                  <ul>
                    {fam.items.map((s) => {
                      const isCurrent = s.code === currentCode;
                      return (
                        <li key={s.code}>
                          <button
                            type="button"
                            onClick={() => pick(s.code)}
                            data-testid={`sector-preview-option-${s.code}`}
                            className={`w-full flex items-center justify-between gap-2 px-2 py-1 rounded text-[12px] text-left transition-colors ${
                              isCurrent
                                ? "bg-slate-900 text-white"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <span className="flex-1 truncate">
                              {s.display_name}
                            </span>
                            {isCurrent && (
                              <span className="text-[9px] font-semibold uppercase tracking-wider text-white/70">
                                atual
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * Banner no topo quando o preview está ativo.
 * v0.99.48 — removido o fundo bege/amber. Paleta slate/dark discreta.
 */
export function SectorPreviewBanner() {
  const { user } = useAuth();
  const { isPreviewMode, profile } = useSector();
  const canUse =
    user?.role === "super_admin" || !!user?.impersonated_by;
  if (!isPreviewMode || !canUse) return null;
  return (
    <div
      className="w-full bg-slate-900 text-slate-100 text-[11.5px] px-4 py-1.5 flex items-center justify-center gap-3"
      data-testid="sector-preview-banner"
    >
      <span className="uppercase tracking-[0.14em] text-[10px] opacity-70">
        Preview
      </span>
      <span className="font-medium">
        {profile?.display_name || profile?.code}
      </span>
      <span className="opacity-50">·</span>
      <span className="opacity-70">nada é gravado no banco</span>
      <button
        type="button"
        onClick={() => setSectorPreviewCode(null)}
        className="ml-2 text-[10.5px] font-semibold underline decoration-slate-400 hover:decoration-white"
        data-testid="sector-preview-banner-exit"
      >
        Sair
      </button>
    </div>
  );
}
